"use client";

/**
 * @fileoverview Web Vitals reporting component for Next.js 15.
 *
 * This component uses the web-vitals library to track Core Web Vitals
 * and report them for performance monitoring. It supports the perf-001
 * requirement (page loads under 3 seconds).
 */

import { useEffect } from "react";
import type { Metric } from "@/lib/performance-metrics";
import { reportWebVitals } from "@/lib/performance-metrics";
import * as Sentry from "@sentry/browser";

/**
 * WebVitals component for client-side performance tracking.
 *
 * @description This component initializes Web Vitals tracking using the
 * web-vitals library. It measures and reports:
 * - LCP (Largest Contentful Paint)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 * - CLS (Cumulative Layout Shift)
 * - INP (Interaction to Next Paint)
 *
 * The component should be included once at the root layout level.
 *
 * @returns {null} This component doesn't render anything
 *
 * @example In app/layout.tsx
 * ```tsx
 * import { WebVitals } from "@/components/performance/WebVitals";
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <WebVitals />
 *         {children}
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function WebVitals() {
  useEffect(() => {
    // Dynamically import web-vitals to avoid SSR issues
    const enrichSentry = (metric: any) => {
      Sentry.setContext("webVitals", {
        [metric.name]: metric.value,
        [`${metric.name}_rating`]: metric.rating,
      });
    };

    import("web-vitals").then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
      // Report each metric as it becomes available
      onCLS((metric) => {
        reportWebVitals(metric as unknown as Metric);
        enrichSentry(metric);
      });
      onFCP((metric) => {
        reportWebVitals(metric as unknown as Metric);
        enrichSentry(metric);
      });
      onINP((metric) => {
        reportWebVitals(metric as unknown as Metric);
        enrichSentry(metric);
      });
      onLCP((metric) => {
        reportWebVitals(metric as unknown as Metric);
        enrichSentry(metric);
      });
      onTTFB((metric) => {
        reportWebVitals(metric as unknown as Metric);
        enrichSentry(metric);
      });
    });
  }, []);

  return null;
}
